import { useMemo, type ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { Card, EmptyState, Eyebrow, Money, PnlNumber, Skeleton, UiLink, cx, formatCount, paths } from '@algoboo/ui'
import { brainsQuery, request, statsQuery, useBrains, useStats, useWeekStandings, weekStandingsQuery } from '../lib/api'
import { prefetch } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { activityMix, brainUsage, summariseBooks, sumUsd, type BookSummary } from '../lib/stats'
import { PageHeader } from '../components/Shell'
import { AgentAvatar, QueryError } from '../components/widgets'

export const Route = createFileRoute('/stats')({
  loader: ({ context: { queryClient: qc } }) =>
    prefetch(
      qc.ensureQueryData(statsQuery()),
      qc.ensureQueryData(weekStandingsQuery()),
      qc.ensureQueryData(agentsSample()),
      qc.ensureQueryData(tokensSample()),
      qc.ensureQueryData(activitySample()),
      qc.ensureQueryData(brainsQuery()),
    ),
  component: StatsPage,
})

/** Largest page the directory and token reads allow; the page says when a figure covers only this many rows. */
const SAMPLE = 200
const ACTIVITY_SAMPLE = 100

const agentsSample = () => queryOptions({ queryKey: ['stats', 'agents-sample'], queryFn: ({ signal }) => request('listAgents', { signal, query: { range: 'all', sort: 'new', limit: SAMPLE } }), staleTime: 60_000 })
const tokensSample = () => queryOptions({ queryKey: ['stats', 'tokens-sample'], queryFn: ({ signal }) => request('listTokens', { signal, query: { sort: 'volume', limit: SAMPLE } }), staleTime: 60_000 })
const activitySample = () => queryOptions({ queryKey: ['stats', 'activity-sample'], queryFn: ({ signal }) => request('listActivity', { signal, query: { limit: ACTIVITY_SAMPLE } }), staleTime: 30_000 })

const KIND_LABEL: Record<string, string> = {
  trade: 'Trades',
  launch: 'Token launches',
  epoch_closed: 'Weeks of holder fees closed',
  reward_claimed: 'Rewards claimed',
  agent_activated: 'Agents activated',
  milestone: 'Milestones',
}

function StatsPage() {
  usePageMeta('Stats', 'Platform totals for algo.boo: agents, trades, volume, P&L by book and what token holders were paid, from the recorded trades and fees.')
  const stats = useStats()
  const board = useWeekStandings()
  const books = useMemo(() => summariseBooks(board.data), [board.data])
  const agents = useQuery(agentsSample())
  const tokens = useQuery(tokensSample())
  const activity = useQuery(activitySample())
  const brains = useBrains()
  const s = stats.data

  return (
    <>
      <PageHeader
        eyebrow="Floor"
        title="Stats"
        lede="Totals from the recorded trades and fees. Each figure names its window and its source; live and paper books are never added together, and nothing here is estimated."
      />

      <section aria-labelledby="totals-h" data-testid="stats-totals">
        <h2 id="totals-h" className="sr-only">
          Platform totals
        </h2>
        {stats.error ? (
          <QueryError error={stats.error} refetch={() => void stats.refetch()} compact={false} />
        ) : (
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line md:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Agents" hint={s ? `${formatCount(s.activeAgents)} active now` : null} value={s ? formatCount(s.agents) : null} />
            <Kpi label="Trades" hint="filled orders, last 24 h" value={s ? formatCount(s.trades24h) : null} />
            <Kpi label="Volume" hint="live orders, last 24 h" value={s ? <Money value={s.volume24hUsd} compact /> : null} />
            <Kpi label="Token holders" hint="across agent tokens" value={s ? formatCount(s.holders) : null} />
            <Kpi label="Paid to holders" hint="closed weeks, all time" value={s ? <Money value={s.distributedUsd} compact /> : null} tone="gain" />
            <Kpi
              label="Tokens launched"
              hint={tokens.data ? (tokens.data.nextCursor ? `the first ${SAMPLE} by volume` : 'live and halted') : null}
              value={tokens.data ? `${formatCount(tokens.data.items.length)}${tokens.data.nextCursor ? '+' : ''}` : tokens.error ? '—' : null}
            />
          </div>
        )}
      </section>

      {s && s.tradesAll !== undefined && <AllTime s={s} />}

      <section className="mt-8" aria-labelledby="books-h" data-testid="stats-books">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="books-h" className="font-display text-[18px] font-semibold">
              Books
            </h2>
            <p className="text-[13px] text-ink-3">
              Every listed agent's net over the last seven days in ETH, from the platform's standings; an agent whose result is not reported yet is not added. The best is the platform's first place, by its one ranking rule.
            </p>
          </div>
        </div>
        {board.error ? (
          <QueryError error={board.error} refetch={() => void board.refetch()} compact={false} />
        ) : board.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-44 rounded-xl" />
            <Skeleton className="h-44 rounded-xl" />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <BookCard title="Live" note="Customer wallets on Robinhood Chain, signed permissions." book={books.live} />
            <BookCard title="Paper" note="Simulated: dry runs through the trading service, same orders, no funds moved." book={books.paper} />
          </div>
        )}
      </section>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <Card as="section" className="p-5" aria-labelledby="brains-h" data-testid="stats-brains">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="brains-h" className="font-display text-[18px] font-semibold">
              Brains in use
            </h2>
            <UiLink href="/brains" className="text-[12.5px] font-medium text-accent hover:underline">
              Catalog
            </UiLink>
          </div>
          {agents.error ? (
            <QueryError error={agents.error} refetch={() => void agents.refetch()} />
          ) : brains.data && brains.data.length > 0 && brains.data.every((b) => b.agentsCount !== undefined) ? (
            <CatalogBrainBars brains={brains.data} />
          ) : !agents.data ? (
            <Skeleton className="mt-3 h-32" />
          ) : agents.data.items.length === 0 ? (
            <EmptyState compact title="No agents yet" body="The first agent's brain will show up here." />
          ) : (
            <BrainBars agents={agents.data.items} partial={!!agents.data.nextCursor} names={Object.fromEntries((brains.data ?? []).map((b) => [b.id, b.displayName]))} />
          )}
        </Card>

        <Card as="section" className="p-5" aria-labelledby="activity-h" data-testid="stats-activity">
          <h2 id="activity-h" className="font-display text-[18px] font-semibold">
            Recent activity
          </h2>
          {activity.error ? (
            <QueryError error={activity.error} refetch={() => void activity.refetch()} />
          ) : !activity.data ? (
            <Skeleton className="mt-3 h-32" />
          ) : activity.data.items.length === 0 ? (
            <EmptyState compact title="Nothing recorded yet" body="Trades, launches, closed weeks and claims appear here as they happen." />
          ) : (
            <ActivityBreakdown items={activity.data.items} />
          )}
        </Card>
      </div>

      <p className="mt-8 text-[12px] text-ink-3">
        Sources: algo.boo&apos;s totals, the platform&apos;s standings of the week, the agent and token directories and the activity list, refreshed every 30 to 60 seconds.
      </p>
    </>
  )
}

type Stats = NonNullable<ReturnType<typeof useStats>['data']>

const FEE_LABEL: Record<string, string> = { execution: 'Execution', performance: 'Performance', subscription: 'Subscriptions', launch: 'Launch' }

function AllTime({ s }: { s: Stats }) {
  const daily = s.daily ?? []
  const max = Math.max(1, ...daily.map((d) => d.trades))
  return (
    <section className="mt-8" aria-labelledby="alltime-h" data-testid="stats-alltime">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="alltime-h" className="font-display text-[18px] font-semibold">
            All time
          </h2>
          <p className="text-[13px] text-ink-3">
            Since launch, from the recorded orders and fees and the closed weeks of the holders&apos; fee contract. Cached for 60 s{s.asOf ? `, as of ${new Date(s.asOf).toLocaleTimeString()}` : ''}.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line md:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Trades" hint="filled orders, all books" value={s.tradesAll !== undefined ? formatCount(s.tradesAll) : '—'} />
        <Kpi label="Volume" hint="live orders" value={<Money value={s.volumeAllUsd ?? null} compact />} />
        <Kpi label="Fees collected" hint="every fee category" value={<Money value={s.feesCollectedUsd ?? null} compact />} />
        <Kpi label="Distributed" hint="to token holders" value={<Money value={s.distributedUsd} compact />} tone="gain" />
        <Kpi label="Weeks closed" hint={s.claimsCount !== undefined ? `${formatCount(s.claimsCount)} claims` : null} value={s.epochsClosed !== undefined ? formatCount(s.epochsClosed) : '—'} />
        <Kpi label="Trading agents" hint={`last 30 days · ${formatCount(s.activeAgents)} active`} value={s.tradingAgents30d !== undefined ? formatCount(s.tradingAgents30d) : '—'} />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="p-5" data-testid="stats-daily">
          <h3 className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Trades per day, last 30 days</h3>
          {daily.length === 0 ? (
            <p className="mt-3 text-[13px] text-ink-3">No trades in the last 30 days.</p>
          ) : (
            <ol className="mt-4 flex h-28 items-end gap-[3px]" aria-label="Trades per day">
              {daily.map((d) => (
                <li key={d.day} className="flex h-full min-w-0 flex-1 items-end" title={`${d.day}: ${d.trades} trades, $${d.volumeUsd}`}>
                  <span className="sr-only">
                    {d.day}: {d.trades} trades
                  </span>
                  <span className="block w-full rounded-t-[2px] bg-accent/70" style={{ height: `${Math.max(2, (d.trades / max) * 100)}%` }} aria-hidden="true" />
                </li>
              ))}
            </ol>
          )}
          {daily.length > 0 && (
            <div className="mt-2 flex justify-between font-mono text-[11px] text-ink-3">
              <span>{daily[0]!.day}</span>
              <span>{daily[daily.length - 1]!.day}</span>
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="stats-fees">
          <h3 className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Fees by category</h3>
          {(s.feesByCategory ?? []).length === 0 ? (
            <p className="mt-3 text-[13px] text-ink-3">No fees collected yet.</p>
          ) : (
            <dl className="mt-2 divide-y divide-line">
              {(s.feesByCategory ?? []).map((f) => (
                <div key={f.category} className="flex items-center justify-between py-2 text-[13px]">
                  <dt className="text-ink-2">{FEE_LABEL[f.category] ?? f.category}</dt>
                  <dd>
                    <Money value={f.amountUsd} compact />
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>
    </section>
  )
}

function CatalogBrainBars({ brains }: { brains: readonly { id: string; displayName: string; agentsCount?: number | undefined }[] }) {
  const rows = [...brains].filter((b) => (b.agentsCount ?? 0) > 0).sort((a, b) => (b.agentsCount ?? 0) - (a.agentsCount ?? 0))
  const max = Math.max(1, ...rows.map((b) => b.agentsCount ?? 0))
  if (rows.length === 0) return <EmptyState compact title="No agents yet" body="The first agent's brain will show up here." />
  return (
    <>
      <ul className="mt-3 space-y-2.5" aria-label="Agents per brain">
        {rows.slice(0, 8).map((b) => (
          <li key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
            <UiLink href={`/agents?brain=${encodeURIComponent(b.id)}`} className="rh-hit min-w-0 text-[13px] hover:underline">
              <span className="block truncate">{b.displayName}</span>
            </UiLink>
            <span className="num font-mono text-[12px] text-ink-2">{formatCount(b.agentsCount ?? 0)}</span>
            <span className="col-span-2 block h-[3px] overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
              <span className="block h-full rounded-full bg-ink-3" style={{ width: `${Math.max(3, ((b.agentsCount ?? 0) / max) * 100)}%` }} />
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-ink-3">Every public agent, by brain.</p>
    </>
  )
}

function Kpi({ label, value, hint, tone }: { label: string; value: ReactNode | null; hint: ReactNode | null; tone?: 'gain' }) {
  return (
    <div className="min-w-0 bg-surface p-4">
      <Eyebrow>{label}</Eyebrow>
      <div className={cx('font-display num mt-1.5 truncate text-[22px] font-semibold leading-tight', tone === 'gain' ? 'text-gain' : 'text-ink')}>{value ?? <Skeleton className="h-6 w-20" />}</div>
      <div className="mt-1 truncate text-[11.5px] text-ink-3">{hint ?? ' '}</div>
    </div>
  )
}

function BookCard({ title, note, book }: { title: string; note: string; book: BookSummary }) {
  return (
    <Card className="p-5" data-testid={`book-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <span className={cx('h-1.5 w-1.5 rounded-full', title === 'Live' ? 'bg-accent' : 'bg-ink-4')} aria-hidden="true" />
        <h3 className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">{title} book</h3>
        <span className="ml-auto font-mono text-[11px] text-ink-3">7 days</span>
      </div>
      {book.agents === 0 ? (
        <p className="mt-4 text-[13.5px] text-ink-3">No {title.toLowerCase()} results this week yet. {note}</p>
      ) : (
        <>
          <div className="mt-3">
            {book.pnlEth === null ? (
              <p className="text-[13.5px] text-ink-3">Net unavailable: the platform reports no result for these agents this week yet.</p>
            ) : (
              <PnlNumber value={book.pnlEth} unit="eth" size="lg" />
            )}
          </div>
          <dl className="mt-4 grid grid-cols-3 gap-3 text-[13px]">
            <div>
              <dt className="text-[11px] uppercase tracking-[0.06em] text-ink-3">Agents</dt>
              <dd className="num mt-0.5 font-medium">{formatCount(book.agents)}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.06em] text-ink-3">In profit</dt>
              <dd className="num mt-0.5 font-medium">
                {formatCount(book.inProfit)}
                <span className="text-ink-3"> / {formatCount(book.inLoss)} down</span>
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-[0.06em] text-ink-3">Not known yet</dt>
              <dd className="num mt-0.5 font-medium">{formatCount(book.unknown)}</dd>
            </div>
          </dl>
          {book.best && (
            <UiLink href={paths.agent(book.best.agent.handle)} className="mt-4 flex items-center gap-3 rounded-lg border border-line px-3 py-2 hover:border-line-strong">
              <AgentAvatar handle={book.best.agent.handle} name={book.best.agent.name} avatar={book.best.agent.avatar} avatarUrl={book.best.agent.avatarUrl} size={28} />
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-3">First place this week</span>
                <span className="block truncate text-[13.5px] font-medium">{book.best.agent.name}</span>
              </span>
              <PnlNumber value={book.best.netEth} unit="eth" compact size="sm" />
            </UiLink>
          )}
          <p className="mt-3 text-[12px] text-ink-3">{note}</p>
        </>
      )}
    </Card>
  )
}

function BrainBars({ agents, partial, names }: { agents: readonly { brainId: string | null }[]; partial: boolean; names: Record<string, string> }) {
  const usage = [...brainUsage(agents).entries()]
  const max = Math.max(1, ...usage.map(([, n]) => n))
  return (
    <>
      <ul className="mt-3 space-y-2.5" aria-label="Agents per brain">
        {usage.slice(0, 8).map(([id, n]) => (
          <li key={id ?? 'external'} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
            <span className="truncate text-[13px]">{id ? (names[id] ?? id) : 'External agents'}</span>
            <span className="num font-mono text-[12px] text-ink-2">{formatCount(n)}</span>
            <span className="col-span-2 block h-[3px] overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
              <span className="block h-full rounded-full bg-ink-3" style={{ width: `${Math.max(3, (n / max) * 100)}%` }} />
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-ink-3">{partial ? `The newest ${SAMPLE} public agents.` : `All ${formatCount(agents.length)} public agents.`}</p>
    </>
  )
}

function ActivityBreakdown({ items }: { items: Parameters<typeof activityMix>[0] }) {
  const mix = activityMix(items)
  const span = mix.from && mix.to ? Math.max(1, Math.round((Date.parse(mix.to) - Date.parse(mix.from)) / 3_600_000)) : null
  return (
    <>
      <dl className="mt-3 divide-y divide-line">
        {mix.byKind.map((k) => (
          <div key={k.kind} className="flex items-center justify-between py-2 text-[13px]">
            <dt className="text-ink-2">{KIND_LABEL[k.kind] ?? k.kind}</dt>
            <dd className="num font-mono">{formatCount(k.count)}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between py-2 text-[13px]">
          <dt className="text-ink-2">Traded notional</dt>
          <dd>
            <Money value={sumUsd([mix.volumeUsd])} compact />
          </dd>
        </div>
      </dl>
      <p className="mt-2 text-[12px] text-ink-3">
        The last {formatCount(mix.total)} events{span ? `, about ${formatCount(span)} h` : ''}. The live activity runs under the header.
      </p>
    </>
  )
}
